// Similar to https://material-ui.com/components/avatars/

import React, { useMemo, ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { numberInRange } from '~/services/utils/propValidators';
import { ValuesOf } from '~/types/utils';

const SHAPES = {
	CIRCLE: 'circle',
	SQUARE: 'square',
} as const;

const shapeRadiusMap = {
	[SHAPES.CIRCLE]: '50%',
	[SHAPES.SQUARE]: '5%',
} as const;

type Shape = ValuesOf<typeof SHAPES>;

interface WrapProps {
	bgColor?: string;
	hasImage: boolean;
	onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
	shape: Shape;
	size: number;
}

interface InnerCircleProps {
	shape: Shape;
}

interface ImageProps {
	brightness?: number;
	imageScaleDown?: boolean;
	imagePadding?: string;
	shape: Shape;
	disableImageShape?: boolean;
}

interface NextImageWrapProps {
	size: number;
}

export interface AvatarProps {
	/** Image alt */
	alt?: string;
	bgColor?: string;
	brightness?: number;
	children?: ReactNode;
	className?: string;
	disableImageShape?: boolean;
	/** Icon to display when no src is provided */
	icon?: ReactNode;
	imageDataTestId?: string;
	imageScaleDown?: boolean;
	onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
	/** Shape of the avatar */
	shape?: Shape;
	/** Image size */
	size?: number;
	/** Image source */
	src?: string;
	/** Text to display when no image/icon provided */
	text?: string | ReactNode;
	NextImage?: React.ElementType;
}

const Wrap = styled.div<WrapProps>`
	width: ${props => props.size}px;
	height: ${props => props.size}px;
	position: relative;
	border-radius: ${props => shapeRadiusMap[props.shape] || '50%'};
	color: ${props => props.theme.colors.text.main};
	background-color: ${props => !props.hasImage && props.theme.colors.grey[200]};
	font-weight: ${props => props.theme.text.fontWeightSemiBold};
	font-size: 20px;

	${({ bgColor }) =>
		bgColor &&
		css`
			background-color: ${bgColor};
		`}
`;

const InnerCircle = styled.div<InnerCircleProps>`
	width: 100%;
	height: 100%;
	border-radius: ${props => shapeRadiusMap[props.shape] || '50%'};
	overflow: hidden;
	text-align: center;
	display: flex;
	justify-content: center;
	align-items: center;
`;

const Image = styled.img<ImageProps>`
	width: 100%;
	height: 100%;
	position: absolute;
	top: 0;
	left: 0;
	object-fit: ${props => (props.imageScaleDown ? 'scale-down' : 'cover')};
	padding: ${props => props.imageScaleDown && props.imagePadding};
	border-radius: ${props =>
		props.disableImageShape ? '0%' : shapeRadiusMap[props.shape] || '50%'};
	filter: ${props => props.brightness && `brightness(${props.brightness})`};
`;

const NextImageWrapper = styled.div<NextImageWrapProps>`
	width: ${({ size }) => size}px;
	height: ${({ size }) => size}px;
	position: relative;
	border-radius: 50%;
	overflow: hidden;
`;

const DEFAULT_PROPS = {
	disableImageShape: false,
	alt: 'avatar',
	shape: SHAPES.CIRCLE,
	size: 42,
} as const;

const Avatar = ({
	alt = DEFAULT_PROPS.alt,
	bgColor,
	brightness,
	children,
	className,
	disableImageShape,
	icon,
	imageScaleDown,
	imageDataTestId,
	onClick,
	shape = DEFAULT_PROPS.shape,
	size = DEFAULT_PROPS.size,
	src,
	text,
	NextImage,
}: AvatarProps) => {
	const innerContent = useMemo(() => {
		if (src) {
			return NextImage ? (
				<NextImageWrapper size={size}>
					<NextImage
						src={src}
						objectFit="cover"
						sizes={`${2 * size}px`}
						layout="fill"
					/>
				</NextImageWrapper>
			) : (
				<Image
					alt={alt}
					brightness={brightness}
					data-testid={imageDataTestId}
					disableImageShape={disableImageShape}
					imagePadding={disableImageShape ? '8px' : '5px'}
					imageScaleDown={imageScaleDown}
					shape={shape}
					src={src}
				/>
			);
		}

		if (icon) {
			return icon;
		}

		return text;
	}, [
		NextImage,
		alt,
		brightness,
		disableImageShape,
		icon,
		imageDataTestId,
		imageScaleDown,
		shape,
		size,
		src,
		text,
	]);

	return (
		<Wrap
			bgColor={bgColor}
			className={className}
			hasImage={!!src}
			onClick={onClick}
			shape={shape}
			size={size}
		>
			<InnerCircle shape={shape}>{innerContent}</InnerCircle>
			{children}
		</Wrap>
	);
};

Avatar.propTypes = {
	brightness: (props, propName, componentName) =>
		numberInRange({
			props,
			propName,
			componentName,
			isRequired: false,
			rangeStart: 0,
			rangeEnd: 1,
		}),
};

Avatar.defaultProps = DEFAULT_PROPS;

export default Avatar;
